/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @angular-eslint/prefer-inject */
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { FileContent, FileNode } from '@nebula/shared-vault/domain';
import { MockFileService } from './mock-file.service';
import { Observable } from 'rxjs';
import { EmptyComponent, SearchComponent, IconComponent } from 'nebula-ui-kit';
import { FolderActionsComponent } from '../folder-actions/folder-actions.component';

export interface VirtualFolderPreview {
  name: string;
  files: FileNode[];
  loading?: boolean;
  showExitZip?: boolean;
}

@Component({
  selector: 'app-file-tree',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    EmptyComponent,
    SearchComponent,
    IconComponent,
    FolderActionsComponent,
  ],
  templateUrl: './file-tree.component.html',
  styleUrls: ['./file-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.title]': 'null',
  },
})
export class FileTreeComponent implements OnInit, OnChanges {
  private static readonly SEARCH_RESULTS_PREVIEW_PATH = '__repository_search_results_preview__';
  private static readonly EXPLORER_ROOT_PATH = '__repository_explorer_root__';
  @Input() rootPath?: string;
  @Input() showFilePreview = true;
  @Input() fileTreeData?: FileNode[];
  /** Ruta seleccionada controlada por el padre (rehidratación tras refresh). */
  @Input() selectedPathExternal: string | null = null;
  /** Archivo resaltado en el panel (independiente de la carpeta activa). */
  @Input() selectedFilePathExternal: string | null = null;
  /** Si el padre controla plegar/expandir (p. ej. caché + señal); si no, se muta `expanded` en el nodo local. */
  @Input() useExternalExpand = false;
  /** Resultados de búsqueda global: el filtro del árbol incluye archivos y re-selecciona la raíz al cambiar datos. */
  @Input() flatSearchResults = false;
  /** Si true, el buscador del árbol delega en el padre (API); no filtra solo nombres de carpeta. */
  @Input() globalFileSearch = false;
  /** Sincroniza el texto del buscador con el padre (p. ej. Búsqueda global de Bóveda). */
  @Input() externalSearchTerm = '';
  /** Carga de búsqueda global en curso (padre). */
  @Input() searchLoading = false;
  /** Si el usuario puede asignar archivos (superadmin o administrador de empresa). */
  @Input() canAssign = false;
  /** Predicado opcional: solo rutas OAIS DIP u otras políticas de asignación. */
  @Input() assignablePathPredicate?: (path: string) => boolean;
  /** Vista previa en panel derecho sin cambiar el árbol (p. ej. contenido de un ZIP). */
  @Input() virtualFolderPreview: VirtualFolderPreview | null = null;
  @Input() fileContentProvider?: (node: FileNode) => Observable<FileContent>;
  @Output() fileSelected = new EventEmitter<FileNode>();
  @Output() fileDownloaded = new EventEmitter<FileNode>();
  @Output() assignFile = new EventEmitter<FileNode>();
  @Output() exitVirtualFolder = new EventEmitter<void>();
  @Output() folderSelected = new EventEmitter<FileNode>();
  @Output() searchChanged = new EventEmitter<string>();
  @Output() searchSubmitted = new EventEmitter<string>();
  @Output() treeExpandAction = new EventEmitter<{
    path: string;
    action: 'load' | 'toggle';
  }>();

  fileTree: FileNode[] = [];
  /** Ruta de carpeta seleccionada en el árbol / panel derecho. */
  selectedPath: string | null = null;
  /** Archivo resaltado en las tarjetas del panel. */
  selectedFilePath: string | null = null;
  selectedFolder: FileNode | null = null;
  isLoading = false;
  searchTerm = '';
  isPanelCollapsed = false;

  constructor(
    private readonly mockFileService: MockFileService,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedPathExternal']) {
      this.selectedPath = this.selectedPathExternal ?? null;
    }
    if (changes['selectedFilePathExternal']) {
      this.selectedFilePath = this.selectedFilePathExternal ?? null;
    }
    if (changes['externalSearchTerm']) {
      const next = changes['externalSearchTerm'].currentValue ?? '';
      if (next !== this.searchTerm) {
        this.searchTerm = next;
      }
    }
    if (changes['fileTreeData'] && Array.isArray(this.fileTreeData)) {
      this.applyFileTreeData();
    } else if (changes['selectedPathExternal'] || changes['selectedFilePathExternal'] || changes['flatSearchResults']) {
      this.syncSelectedFolderFromTree();
    }
    if (
      changes['selectedPathExternal'] ||
      changes['selectedFilePathExternal'] ||
      changes['fileTreeData'] ||
      changes['flatSearchResults']
    ) {
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    if (Array.isArray(this.fileTreeData)) {
      this.applyFileTreeData();
      return;
    }
    if (!this.flatSearchResults) {
      this.loadFileTree();
      return;
    }
    this.isLoading = false;
  }

  private applyFileTreeData(): void {
    this.fileTree = this.fileTreeData ?? [];
    this.isLoading = false;
    this.syncSelectedFolderFromTree();
  }

  private syncSelectedFolderFromTree(): void {
    if (this.flatSearchResults) {
      const files = this.fileTree.filter((n) => n.type === 'file');
      if (files.length > 0) {
        this.selectedFolder = {
          name: `Resultados (${files.length})`,
          path: FileTreeComponent.SEARCH_RESULTS_PREVIEW_PATH,
          type: 'directory',
          expanded: true,
          children: files,
        };
        this.selectedPath = FileTreeComponent.SEARCH_RESULTS_PREVIEW_PATH;
      } else {
        this.selectedFolder = null;
        this.selectedPath = null;
      }
      return;
    }
    const pathToSelect = this.selectedPathExternal ?? this.selectedPath;
    if (!this.fileTree.length) {
      return;
    }
    if (pathToSelect) {
      this.selectedPath = pathToSelect;
      const folder = this.resolveFolderForSelection(pathToSelect);
      if (folder) {
        this.selectedFolder = folder;
        return;
      }
      if (
        this.selectedFolder &&
        this.normalizeRepoPath(this.selectedFolder.path) === this.normalizeRepoPath(pathToSelect)
      ) {
        return;
      }
    }
    if (this.selectedFolder) {
      return;
    }
    const root =
      this.fileTree.length === 1 && this.fileTree[0]?.type === 'directory'
        ? this.fileTree[0]
        : null;
    if (root) {
      this.selectedFolder = root;
      this.selectedPath = root.path;
    }
  }

  private normalizeRepoPath(path: string | null | undefined): string {
    return (path ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/').replace(/\/$/, '');
  }

  /** Carpeta cuyo listado debe mostrarse en el panel derecho (archivo → carpeta padre). */
  private resolveFolderForSelection(path: string): FileNode | null {
    const normalized = this.normalizeRepoPath(path);
    if (!normalized) {
      return null;
    }
    if (normalized === FileTreeComponent.EXPLORER_ROOT_PATH) {
      return this.findNodeByPath(this.fileTree, normalized);
    }

    if (this.isRepositoryFilePath(normalized)) {
      const parentPath = this.parentPathFromFilePath(normalized);
      if (parentPath) {
        const parent = this.findNodeByPath(this.fileTree, parentPath);
        if (parent?.type === 'directory') {
          return parent;
        }
      }
    }

    const node = this.findNodeByPath(this.fileTree, normalized);
    if (node?.type === 'directory') {
      return node;
    }

    if (this.selectedFolder && this.folderContainsPath(this.selectedFolder, normalized)) {
      return this.selectedFolder;
    }

    const parentPath = this.parentPathFromFilePath(normalized);
    if (parentPath) {
      const parent = this.findNodeByPath(this.fileTree, parentPath);
      if (parent?.type === 'directory') {
        return parent;
      }
    }

    return null;
  }

  private parentPathFromFilePath(path: string): string {
    const normalized = this.normalizeRepoPath(path);
    if (!normalized || normalized === FileTreeComponent.EXPLORER_ROOT_PATH) {
      return '';
    }
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length <= 1) {
      return FileTreeComponent.EXPLORER_ROOT_PATH;
    }
    parts.pop();
    return parts.join('/');
  }

  private folderContainsPath(folder: FileNode, path: string): boolean {
    const normalized = this.normalizeRepoPath(path);
    if (this.normalizeRepoPath(folder.path) === normalized) {
      return true;
    }
    return this.getFolderFiles(folder).some(
      (file) => this.normalizeRepoPath(file.path) === normalized,
    );
  }

  loadFileTree(): void {
    this.isLoading = true;
    if (Array.isArray(this.fileTreeData)) {
      this.applyFileTreeData();
      return;
    }
    this.mockFileService.getFileTree().subscribe({
      next: (tree) => {
        this.fileTree = tree;
        this.isLoading = false;
        this.syncSelectedFolderFromTree();
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchChanged.emit(value);
    this.cdr.markForCheck();
  }

  onSearchSubmit(value: string): void {
    this.searchTerm = value;
    this.searchSubmitted.emit(value);
    this.cdr.markForCheck();
  }

  hasChildren(node: FileNode): boolean {
    if (node.type !== 'directory') return false;
    if (node.childrenLazy) return true;
    if (!node.children?.length) return false;
    return node.children.some((child: FileNode) => child.type === 'directory');
  }

  isNodeSelected(node: FileNode): boolean {
    if (!this.selectedPath) return false;
    return node.path === this.selectedPath;
  }

  private findNodeByPath(nodes: FileNode[] | undefined, path: string): FileNode | null {
    if (!nodes?.length) return null;
    const target = this.normalizeRepoPath(path);
    for (const n of nodes) {
      if (this.normalizeRepoPath(n.path) === target) {
        return n;
      }
      if (n.type === 'directory' && n.children?.length) {
        const c = this.findNodeByPath(n.children, path);
        if (c) return c;
      }
    }
    return null;
  }

  private isRepositoryFilePath(path: string): boolean {
    const base = path.split('/').filter(Boolean).pop() ?? '';
    return /\.[a-z0-9]{2,8}$/i.test(base);
  }

  handleFileClick(file: FileNode, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedFilePath = file.path ?? null;
    if (this.flatSearchResults) {
      const files = this.filteredTree.filter((n) => n.type === 'file');
      this.selectedPath = FileTreeComponent.SEARCH_RESULTS_PREVIEW_PATH;
      this.selectedFolder = {
        name: `Resultados (${files.length})`,
        path: FileTreeComponent.SEARCH_RESULTS_PREVIEW_PATH,
        type: 'directory',
        expanded: true,
        children: files.length > 0 ? files : [file],
      };
    }
    this.fileSelected.emit(file);
    this.cdr.markForCheck();
  }

  isFileSelected(node: FileNode): boolean {
    if (node.type !== 'file' || !this.selectedFilePath) {
      return false;
    }
    const a = this.normalizeRepoPath(this.selectedFilePath);
    const b = this.normalizeRepoPath(node.path);
    return a.length > 0 && a === b;
  }

  handleFolderClick(folder: FileNode, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedPath = folder.path;
    this.selectedFolder = folder;
    this.selectedFilePath = null;
    this.folderSelected.emit(folder);
    if (folder.type !== 'directory') {
      return;
    }
    if (!this.hasChildren(folder)) {
      return;
    }
    const isLazy = folder.childrenLazy === true;
    if (isLazy && !folder.expanded) {
      this.treeExpandAction.emit({ path: folder.path, action: 'load' });
      return;
    }
    if (isLazy) {
      this.treeExpandAction.emit({ path: folder.path, action: 'toggle' });
      return;
    }
    if (this.useExternalExpand) {
      this.treeExpandAction.emit({ path: folder.path, action: 'toggle' });
      return;
    }
    folder.expanded = !folder.expanded;
  }

  togglePanelCollapse(): void {
    this.isPanelCollapsed = !this.isPanelCollapsed;
  }

  /** Archivos directos de la carpeta (paridad legacy: no incluir subcarpetas). */
  getFolderFiles(folder: FileNode): FileNode[] {
    if (!folder.children?.length) {
      return [];
    }
    if (this.flatSearchResults) {
      return this.collectFilesInSubtree(folder);
    }
    return folder.children.filter((child: FileNode) => child.type === 'file');
  }

  private collectFilesInSubtree(node: FileNode): FileNode[] {
    if (node.type === 'file') {
      return [node];
    }
    return (node.children ?? []).flatMap((child) => this.collectFilesInSubtree(child));
  }

  getDirectFileCount(folder: FileNode): number {
    if (!folder.children) return 0;
    return folder.children.filter((child: FileNode) => child.type === 'file').length;
  }

  getSubfolderCount(folder: FileNode): number {
    if (!folder.children) return 0;
    return folder.children.filter((child: FileNode) => child.type === 'directory').length;
  }

  getFolderTotalSize(folder: FileNode): string {
    const files = this.getFolderFiles(folder);
    return this.getVirtualFolderTotalSize(files);
  }

  getVirtualFolderTotalSize(files: FileNode[]): string {
    const totalBytes = files.reduce((sum, file) => sum + (file.size || 0), 0);
    return this.formatFileSize(totalBytes);
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  filterTree(nodes: FileNode[], term: string): FileNode[] {
    if (!term) return nodes;
    const searchLower = term.toLowerCase();
    const filterNode = (node: FileNode): FileNode | null => {
      const matchesName = node.name.toLowerCase().includes(searchLower);
      if (node.type === 'file') {
        return matchesName ? node : null;
      }
      if (node.type === 'directory') {
        const filteredChildren = (node.children ?? [])
          .map((child: FileNode) => filterNode(child))
          .filter((child: FileNode | null): child is FileNode => child !== null);
        if (matchesName || filteredChildren.length > 0) {
          return { ...node, expanded: true, children: filteredChildren };
        }
        return null;
      }
      return null;
    };
    return nodes.map((node) => filterNode(node)).filter((node): node is FileNode => node !== null);
  }

  get filteredTree(): FileNode[] {
    if (this.globalFileSearch || this.flatSearchResults) {
      return this.fileTree;
    }
    return this.filterTree(this.fileTree, this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchChanged.emit('');
  }

  getTotalFoldersCount(): number {
    const countFolders = (nodes: FileNode[]): number => {
      let count = 0;
      for (const node of nodes) {
        if (node.type === 'directory') {
          count++;
          if (node.children) {
            count += countFolders(node.children);
          }
        }
      }
      return count;
    };
    return countFolders(this.fileTree);
  }

  getTotalFilesCount(): number {
    const countFiles = (nodes: FileNode[]): number => {
      let count = 0;
      for (const node of nodes) {
        if (node.type === 'file') {
          count++;
        }
        if (node.type === 'directory' && node.children) {
          count += countFiles(node.children);
        }
      }
      return count;
    };
    return countFiles(this.fileTree);
  }

  onOpenFileFromFolder(file: FileNode): void {
    this.selectedFilePath = file.path ?? null;
    this.fileSelected.emit(file);
    this.cdr.markForCheck();
  }

  onDownloadFileFromFolder(file: FileNode): void {
    this.fileDownloaded.emit(file);
  }

  onAssignFileFromFolder(file: FileNode): void {
    const path = this.resolveAssignFilePath(file);
    this.assignFile.emit({ ...file, path });
  }

  /** Ruta OAIS completa para asignación (carpeta activa + nombre si hace falta). */
  private resolveAssignFilePath(file: FileNode): string {
    const raw = (file.path ?? '').trim().replace(/\/+/g, '/');
    if (raw.includes('/')) {
      return raw;
    }
    const base = (this.selectedFolder?.path ?? '').trim().replace(/\/$/, '');
    if (base && base !== '__repository_explorer_root__') {
      const name = (file.name ?? raw).trim();
      if (name) {
        return `${base}/${name}`.replace(/\/+/g, '/');
      }
    }
    return raw;
  }
}
